// launchRules.js (frontend)  — OTS conditional launch-rule evaluator.
//
// Verbatim mirror of backend/utils/launchRules.js (minus the authoring-side
// validateRulesShape, which only the admin PUT needs) so the live launch UI
// (OtsLaunch.js) evaluates a pipeline's `launch_rules` exactly the way the
// backend re-validates them at /ots/launch. Keep the two in lockstep.
//
// A rule: { when:{param, op, value}, then:[{target, kind, effect, values?/value?}] }
// Effects: required | hidden | forbid_values | allow_values | set_value.

function asScalar(v) {
    if (v == null) return "";
    if (Array.isArray(v)) return v.join(",");
    if (typeof v === "boolean") return v ? "true" : "false";
    return String(v);
}
function asArray(v) {
    if (Array.isArray(v)) return v;
    if (v == null || v === "") return [];
    return [v];
}

function matchWhen(when, paramValues) {
    if (!when || !when.param) return false;
    const cur = asScalar(paramValues[when.param]);
    switch (when.op) {
        case "equals": return cur === asScalar(when.value);
        case "not_equals": return cur !== asScalar(when.value);
        case "in": return asArray(when.value).map(asScalar).includes(cur);
        case "not_in": return !asArray(when.value).map(asScalar).includes(cur);
        default: return false;
    }
}

// Per-target effective state: { kind, required, hidden, forcedValue?, allowed:Set|null, forbidden:Set }.
// Combining across firing rules: required=OR, hidden=OR, forbidden=union,
// allowed=intersection, forcedValue=first firing rule wins.
export function effectiveState(rules, paramValues) {
    const state = {};
    if (!Array.isArray(rules)) return state;
    const get = (t, kind) => {
        if (!state[t]) state[t] = { kind: kind || "param", required: false, hidden: false, forcedValue: undefined, allowed: null, forbidden: new Set() };
        return state[t];
    };
    for (const rule of rules) {
        if (!rule || !matchWhen(rule.when, paramValues)) continue;
        for (const eff of rule.then || []) {
            if (!eff || !eff.target) continue;
            const s = get(eff.target, eff.kind);
            switch (eff.effect) {
                case "required": s.required = true; break;
                case "hidden": s.hidden = true; break;
                case "forbid_values": asArray(eff.values).forEach((v) => s.forbidden.add(asScalar(v))); break;
                case "allow_values": {
                    const vals = asArray(eff.values).map(asScalar);
                    s.allowed = s.allowed === null ? new Set(vals) : new Set([...s.allowed].filter((x) => vals.includes(x)));
                    break;
                }
                case "set_value": if (s.forcedValue === undefined) s.forcedValue = asScalar(eff.value); break;
                default: break;
            }
        }
    }
    return state;
}

// Returns a list of human-readable violation messages (empty => OK).
// assignedInputNames = input names that have at least one file assigned.
export function validate(rules, paramValues, assignedInputNames) {
    const assigned = new Set(assignedInputNames || []);
    const state = effectiveState(rules, paramValues);
    const msgs = [];
    for (const [target, s] of Object.entries(state)) {
        if (s.hidden) continue;
        const isInput = s.kind === "input";
        const present = isInput ? assigned.has(target) : asScalar(paramValues[target]) !== "";
        if (s.required && !present) {
            msgs.push(isInput ? `Input "${target}" is required for this configuration.` : `Parameter "${target}" is required for this configuration.`);
            continue;
        }
        if (isInput || !present) continue;
        const sv = asScalar(paramValues[target]);
        if (s.forcedValue !== undefined && sv !== s.forcedValue) {
            msgs.push(`Parameter "${target}" must be "${s.forcedValue}" for this configuration.`);
        } else if (s.forbidden.size && s.forbidden.has(sv)) {
            msgs.push(`Parameter "${target}" cannot be "${sv}" for this configuration.`);
        } else if (s.allowed && !s.allowed.has(sv)) {
            msgs.push(`Parameter "${target}" must be one of: ${[...s.allowed].join(", ")}.`);
        }
    }
    return msgs;
}

export default { effectiveState, validate };
